import styled from '@emotion/styled';
import { Space, Textarea, TextInput, Text } from '@mantine/core';
import { useCallback } from 'react';
import { observer } from 'mobx-react';
import { useAuthZValues } from '@root/Services/AuthorizationService';
import { RuleTypeCard } from './RuleTypeCard';
import { RuleEditCard } from './Design';
import { TagAutomationRule, TagAutomationRuleParametersType } from '@apis/TagManager/model';
import { useDisclosure } from '@mantine/hooks';

function EditRuleForm({ rule }: { rule: TagAutomationRule }) {
    const ruleTypes: TagAutomationRuleParametersType[] = ['AutoTag', 'Replacement', 'SyntaxCorrection', 'Inheritance', 'ResourceGroup', 'BulkTag'];

    const selectType = useCallback(
        (type: TagAutomationRuleParametersType) => {
            if (!rule.Id) {
                rule.Parameters ??= {};
                rule.Parameters.Type = type;
            }
            showErrors();
        },
        [rule]
    );
    const [errorsShown, { open: showErrors }] = useDisclosure(false);

    return (
        <>
            <RuleEditCard onBlur={showErrors}>
                <TextInput
                    data-atid="RuleName"
                    label="Rule Name"
                    error={errorsShown && !rule.RuleName}
                    value={rule.RuleName ?? ''}
                    onChange={(e) => (rule.RuleName = e.currentTarget.value)}
                    maxLength={75}
                />
                <Space h="md" />
                <ResizeableTextarea>
                    <Textarea
                        data-atid="RuleName"
                        label="Rule Description"
                        minRows={1}
                        autosize
                        value={rule.RuleDescription ?? ''}
                        onChange={(e) => (rule.RuleDescription = e.currentTarget.value)}
                        maxLength={300}
                    />
                </ResizeableTextarea>
            </RuleEditCard>
            <Space h="md" />
            {rule.Id ? null : (
                <>
                    <Text mx="1rem" size="lg" weight="bold">
                        Select a Rule Type
                    </Text>
                    <Space h="md" />{' '}
                </>
            )}
            <RuleTypePicker items={ruleTypes.length}>
                {ruleTypes.map((t) => (
                    <RuleTypeCard
                        key={t}
                        disabled={!!rule.Id && rule.Parameters?.Type !== t}
                        selected={rule.Parameters?.Type === t}
                        ruleType={t}
                        onClick={selectType}
                    />
                ))}
            </RuleTypePicker>
        </>
    );
}

export default observer(EditRuleForm);

const ResizeableTextarea = styled.div`
    textarea {
        resize: vertical;
    }
`;

const RuleTypePicker = styled.div<{ items: number }>`
    padding: 0;
    margin-bottom: 12px;
    display: grid;
    grid-template-columns: repeat(${(p) => p.items}, 1fr);
    place-items: center;
    column-gap: 8px;
`;
